import '../Authorization.css';
import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../LanguageContext';
import { getTranslation } from '../consts/translations';

interface VerificationProps {
  setVerificationCode: (id: string) => void;
  onValidateCode: () => void;
  buttonDisabled: boolean;
  loading: boolean;
  verificationError: string;
  sendVerification: () => Promise<void>;
  inputPhoneOrEmail: string;
  onExit: () => void;
  sendAgainClicked: boolean;
  setSendAgainClicked: (sendAgainClicked: boolean) => void;
}

const Verification: React.FC<VerificationProps> = ({
  setVerificationCode,
  onValidateCode,
  buttonDisabled,
  loading,
  verificationError,
  sendVerification,
  inputPhoneOrEmail,
  sendAgainClicked,
  setSendAgainClicked,
}) => {
  const [timer, setTimer] = useState(30);
  const { language } = useLanguage();
  const translation = getTranslation(language);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const otpNavigator = navigator as unknown as {
      credentials?: {
        get: (options: {
          otp: { transport: string[] };
          signal: AbortSignal;
        }) => Promise<{ code?: string } | null>;
      };
    };
    if ('OTPCredential' in window && otpNavigator.credentials) {
      const abort = new AbortController();
      otpNavigator.credentials
        .get({ otp: { transport: ['sms'] }, signal: abort.signal })
        .then((otp) => {
          if (otp?.code) {
            setVerificationCode(otp.code);
            if (inputRef.current) inputRef.current.value = otp.code;
          }
        })
        .catch(() => {
          /* ignored – no OTP or user denied */
        });
      return () => abort.abort();
    }
  }, [setVerificationCode]);

  const handleSendAgainClick = () => {
    if (!sendAgainClicked) {
      setSendAgainClicked(true);
      setTimer(30);
      sendVerification();
    }
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (timer > 0) {
      interval = setInterval(() => setTimer((t) => t - 1), 1000);
    } else if (sendAgainClicked) {
      setSendAgainClicked(false);
    }
    return () => clearInterval(interval);
  }, [sendAgainClicked, setSendAgainClicked, timer]);

  return (
    <div className='login-container'>
      <div className='login-description'>
        {translation.verification_description} – {inputPhoneOrEmail}
      </div>

      <input
        ref={inputRef}
        type='text'
        className='login-input'
        style={{ width: '180px' }}
        placeholder={translation.enter_code_placeholder}
        inputMode='numeric'
        pattern='\d{6}'
        autoComplete='one-time-code'
        onChange={(e) => setVerificationCode(e.target.value)}
      />

      {verificationError && <div className='login-verification-error'>{verificationError}</div>}

      <div
        className='send-again-text'
        style={verificationError ? { marginTop: '10px' } : { marginTop: '20px' }}
      >
        {sendAgainClicked ? (
          <div className='spinner-container'>
            <div className='spinner-wrapper'>
              <div className='spinner'></div>
              <div className='timer'>{timer}</div>
            </div>
            <span className='wait-text'>{translation.wait_for_code}</span>
          </div>
        ) : (
          <>
            <span>{translation.code_not_received}</span>
            <span className='send-again-button' onClick={handleSendAgainClick}>
              {translation.send_code_again}
            </span>
          </>
        )}
      </div>

      <button
        className={`login-button ${loading ? 'loading' : ''}`}
        disabled={loading || !buttonDisabled}
        onClick={onValidateCode}
      >
        {loading ? '' : translation.to_status_page_text}
      </button>
    </div>
  );
};

export default Verification;
